import { FormHelperText, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { useAuthUser } from "react-auth-kit";
import { useGetPatients } from "../../PatientComponents";
import { useGetOrganizations } from "../../HandlingComponents";
import { useGetKits } from "../../KitComponents";
import { prettify } from "../../../utils/utils";
import { resolveEffectiveOrgId } from "../deviceFormUtils";

function AssociationRow({ label, children }) {
  return (
    <Grid container sx={{ alignItems: "center" }}>
      <Grid item xs={6}>
        <Typography>
          <strong>{label}</strong>
        </Typography>
      </Grid>
      <Grid item xs={6}>
        {children}
      </Grid>
    </Grid>
  );
}

function DeviceAssociationFields({
  orgId,
  patientId,
  kitId,
  editMode,
  canEditOrg,
  onFieldsChange,
  associationError,
}) {
  const authUser = useAuthUser();
  const patientsQuery = useGetPatients();
  const organizationsQuery = useGetOrganizations();
  const kitsQuery = useGetKits();

  const patients = patientsQuery.data?.patients ?? [];
  const organizations = organizationsQuery.data?.organizations ?? [];
  const kits = kitsQuery.data?.kits ?? [];

  const isSuperAdmin = authUser()?.role === 1;
  const effectiveOrgId = resolveEffectiveOrgId(authUser, orgId);

  // Superadmin filtra per org selezionata; admin/medico usano già liste scoped dall'API
  const filteredPatients =
    isSuperAdmin && effectiveOrgId
      ? patients.filter((p) => Number(p.org_id) === Number(effectiveOrgId))
      : patients;

  const filteredKits =
    isSuperAdmin && effectiveOrgId
      ? kits.filter(
          (k) =>
            Number(k.org_id) === Number(effectiveOrgId) ||
            Number(k.id) === Number(kitId)
        )
      : kits;

  const orgLabel =
    organizations.find((o) => o.id === orgId)?.name ?? "—";
  const patient = patients.find((p) => Number(p.id) === Number(patientId));
  const patientLabel = patient
    ? `${patient.first_name} ${patient.last_name}`
    : "—";
  const kit = kits.find((k) => Number(k.id) === Number(kitId));
  const kitLabel = kit ? prettify(kit.name) : "—";

  const handleOrgChange = (event) => {
    const newOrgId =
      event.target.value === "" ? null : Number(event.target.value);
    const patch = { org_id: newOrgId };

    if (patientId != null && patientId !== "") {
      const p = patients.find((el) => el.id === patientId);
      if (p && p.org_id !== newOrgId) {
        patch.patient_id = null;
      }
    }
    if (kitId != null && kitId !== "") {
      const k = kits.find((el) => el.id === Number(kitId));
      if (k && newOrgId != null && k.org_id !== newOrgId) {
        patch.kit_id = null;
      }
    }
    onFieldsChange(patch);
  };

  const handlePatientChange = (event) => {
    const newPatientId =
      event.target.value === "" ? null : Number(event.target.value);
    const patch = { patient_id: newPatientId };

    if (newPatientId != null) {
      patch.kit_id = null;
    }

    if (canEditOrg && newPatientId != null) {
      const p = patients.find((el) => el.id === newPatientId);
      if (p?.org_id != null) {
        patch.org_id = p.org_id;
      }
    }
    onFieldsChange(patch);
  };

  const handleKitChange = (event) => {
    const newKitId =
      event.target.value === "" ? null : Number(event.target.value);
    const patch = { kit_id: newKitId };
    if (newKitId != null) {
      patch.patient_id = null;
    }
    if (canEditOrg && newKitId != null) {
      const k = kits.find((el) => el.id === newKitId);
      if (k?.org_id != null) {
        patch.org_id = k.org_id;
      }
    }
    onFieldsChange(patch);
  };

  if (!editMode) {
    return (
      <>
        {canEditOrg && (
          <AssociationRow label="Organizzazione:">
            <Typography>{orgLabel}</Typography>
          </AssociationRow>
        )}
        <AssociationRow label="Paziente:">
          <Typography>{patientLabel}</Typography>
        </AssociationRow>
        <AssociationRow label="Kit:">
          <Typography>{kitLabel}</Typography>
        </AssociationRow>
      </>
    );
  }

  return (
    <>
      <FormHelperText sx={{ mt: 0, mb: 1 }}>
        Associazione: organizzazione, paziente o kit (uno è sufficiente; paziente
        e kit sono esclusivi)
      </FormHelperText>

      {canEditOrg && organizationsQuery.isSuccess && (
        <AssociationRow label="Organizzazione:">
          <TextField
            select
            fullWidth
            margin="dense"
            variant="standard"
            value={orgId ?? ""}
            onChange={handleOrgChange}
          >
            <MenuItem value="">
              <em>Seleziona organizzazione</em>
            </MenuItem>
            {organizations.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </AssociationRow>
      )}

      {patientsQuery.isSuccess && (
        <AssociationRow label="Paziente:">
          <TextField
            select
            fullWidth
            margin="dense"
            variant="standard"
            value={patientId ?? ""}
            onChange={handlePatientChange}
          >
            <MenuItem value="">
              <em>Nessun paziente</em>
            </MenuItem>
            {filteredPatients.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {`${option.first_name} ${option.last_name}`}
              </MenuItem>
            ))}
          </TextField>
        </AssociationRow>
      )}

      {!kitsQuery.isError && (
        <AssociationRow label="Kit:">
          <TextField
            select
            fullWidth
            margin="dense"
            variant="standard"
            value={kitId ?? ""}
            onChange={handleKitChange}
            disabled={kitsQuery.isLoading && kits.length === 0}
            helperText={
              kitsQuery.isLoading && kits.length === 0
                ? "Caricamento kit..."
                : undefined
            }
          >
            <MenuItem value="">
              <em>Nessun kit</em>
            </MenuItem>
            {filteredKits.map((k) => (
              <MenuItem key={k.id} value={k.id}>
                {prettify(k.name)}
                {k.patient_info && k.patient_info !== "Non associato"
                  ? ` (${k.patient_info})`
                  : ""}
              </MenuItem>
            ))}
          </TextField>
        </AssociationRow>
      )}

      {associationError && (
        <FormHelperText error sx={{ mt: 0 }}>
          {associationError}
        </FormHelperText>
      )}
    </>
  );
}

export default DeviceAssociationFields;
